/** A price's last move: up, down or unchanged. */
export type PriceDirection = 'up' | 'down' | 'flat';

export interface PriceParts {
  /** The steady part: "1.08" */
  main: string;
  /** The two digits traders watch, shown larger: "51" */
  big: string;
  /** The last (pipette) digit, shown smaller: "2" */
  small: string;
}

/**
 * Splits a price like MT5 does. With 3 or more decimals the last digit is a small "pipette" and the two digits before it are
 * emphasised: 1.08512 -> "1.08" + "51" + "2". With 2 or fewer decimals the decimals themselves are the emphasised part:
 * 80500.12 -> "80500." + "12". The three parts always join back to the exact price.
 */
export function splitPrice(value: number, digits: number): PriceParts {
  const text = value.toFixed(digits);
  if (digits <= 0) return { main: text, big: '', small: '' };
  if (digits <= 2) {
    const cut = text.length - digits;
    return { main: text.slice(0, cut), big: text.slice(cut), small: '' };
  }
  return { main: text.slice(0, -3), big: text.slice(-3, -1), small: text.slice(-1) };
}

/** Spread in points (the smallest price step of the symbol), as MT5 shows it. */
export const spreadPoints = (bid: number, ask: number, digits: number): number =>
  Math.round((ask - bid) * 10 ** digits);

/** Percentage change since `open`, or null when there is no open price. */
export const changePercent = (price: number, open: number | null | undefined): number | null =>
  open ? ((price - open) / open) * 100 : null;

/** "+0.42%", "-1.05%", or "—" without an open price. */
export const changeText = (change: number | null): string =>
  change === null ? '—' : `${change > 0 ? '+' : ''}${change.toFixed(2)}%`;
